import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

interface HealthStatus {
  status: 'ok' | 'degraded' | 'error';
  databaseAvailable: boolean;
  databaseError: string | null;
  lastChecked: Date;
}

/**
 * Hook to monitor system health, particularly database connectivity
 * Shows a banner when database is unavailable
 */
export function useSystemHealth() {
  const [health, setHealth] = useState<HealthStatus>({
    status: 'ok',
    databaseAvailable: true,
    databaseError: null,
    lastChecked: new Date(),
  });

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const checkHealth = async () => {
      try {
        const response = await apiFetch('/health');
        if (response.ok) {
          const data = await response.json();
          setHealth({
            status: data.status,
            databaseAvailable: data.services?.supabase?.available !== false,
            databaseError: data.services?.supabase?.error || null,
            lastChecked: new Date(),
          });
        }
      } catch (error) {
        // If health endpoint fails, assume degraded status
        setHealth({
          status: 'error',
          databaseAvailable: false,
          databaseError: 'Unable to connect to server',
          lastChecked: new Date(),
        });
      }
    };

    // Check immediately on mount
    checkHealth();

    // Then check every 30 seconds
    intervalId = setInterval(checkHealth, 30000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  return health;
}
